import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { ArrowRight } from "lucide-react";

export function NewsStrip() {
  const { data: posts, isError } = useQuery({
    queryKey: ["home_news"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("news_posts")
        .select("*")
        .eq("is_published", true)
        .order("published_at", { ascending: false })
        .limit(4);
      if (error) throw error;
      return data ?? [];
    },
    retry: 4,
    retryDelay: (attempt) => Math.min(800 * 2 ** attempt, 5000),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  if (isError && !posts?.length) return null;

  return (
    <section className="py-24">
      <div className="container mx-auto px-4">
        <div className="flex items-end justify-between gap-6">
          <div>
            <span className="text-sm font-semibold uppercase tracking-widest text-primary">
              Blogs
            </span>
            <h2 className="mt-3 font-display text-4xl font-semibold leading-tight md:text-5xl">
              The latest from the field
            </h2>
          </div>
          <Link
            to="/news"
            className="hidden items-center gap-2 text-sm font-semibold text-primary hover:gap-3 transition-all md:inline-flex"
          >
            All updates <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {posts?.map((p) => {
            const d = new Date(p.published_at);
            return (
              <Link
                key={p.id}
                to={`/news/${p.slug}`}
                className="group flex flex-col rounded-2xl border border-border bg-card p-6 transition-all hover:-translate-y-1 hover:border-primary/30 hover:shadow-card"
              >
                <div className="font-display text-xs font-semibold uppercase tracking-widest text-primary">
                  {d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
                </div>
                <h3 className="mt-3 font-display text-xl font-semibold leading-snug group-hover:text-primary">
                  {p.title}
                </h3>
                <p className="mt-3 line-clamp-3 text-sm text-muted-foreground">
                  {p.excerpt}
                </p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-primary">
                  Read the full update<span className="sr-only">: {p.title}</span> <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                </span>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
